// src/pages/CreateTestPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next'; // For internationalization
import { useNavigate } from 'react-router-dom'; // For potential navigation after creation
// --- Updated Imports ---
import { useAuth } from '../components/AuthProvider.js'; // To get the current user (teacher)
import { getAllClassrooms } from '../services/classroomService.js'; // To fetch teacher's classrooms
import { createTest } from '../services/testService.js'; // To save the new test
// --- End Updated Imports ---
// Basic styling - create src/styles/CreateTestPage.css or reuse others
import '../styles/ManageClassroomsPage.css'; // Reusing styles for expediency (CSS imports are fine without .js)

const CreateTestPage = () => {
  // Hooks
  const { t } = useTranslation(); // Translation function
  const { currentUser } = useAuth(); // Get logged-in user info
  const navigate = useNavigate(); // Navigation function

  // Component State
  const [title, setTitle] = useState(''); // Test title
  const [dueDate, setDueDate] = useState(''); // Due date (string from input)
  const [selectedClassroomId, setSelectedClassroomId] = useState(''); // ID of the target classroom
  const [questionsText, setQuestionsText] = useState(''); // Raw text for questions
  const [managedClassrooms, setManagedClassrooms] = useState([]); // List of classrooms teacher manages
  const [isLoading, setIsLoading] = useState(false); // Loading state for API calls
  const [error, setError] = useState(''); // Error messages
  const [success, setSuccess] = useState(''); // Success messages

  // Fetch classrooms managed by the current teacher when the component mounts or user changes
  const fetchClassrooms = useCallback(async () => {
    // Ensure a teacher is logged in
    if (currentUser?.uid && currentUser.role === 'teacher') {
      setIsLoading(true); // Indicate loading
      setError(''); // Clear previous errors
      try {
        // Fetch only the classrooms associated with this teacher's UID
        const classrooms = await getAllClassrooms(currentUser.uid);
        setManagedClassrooms(classrooms); // Update state with fetched classrooms
        // Set error message if the teacher manages no classrooms
        if (classrooms.length === 0) {
           setError(t('createTestPage.noClassrooms'));
        }
      } catch (err) {
        // Set error message if fetching fails
        setError(t('createTestPage.errorFetchClassrooms'));
        console.error("Error fetching classrooms:", err);
      } finally {
        setIsLoading(false); // Stop loading indicator
      }
    }
  }, [currentUser, t]); // Dependencies: run when currentUser or translation function changes

  // Run fetchClassrooms on component mount
  useEffect(() => {
    fetchClassrooms();
  }, [fetchClassrooms]); // Dependency array includes the memoized fetch function

  /**
   * Handles form submission to create a new test.
   * @param {React.FormEvent<HTMLFormElement>} e - The form submission event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default browser form submission
    setError(''); // Clear previous errors
    setSuccess(''); // Clear previous success messages

    // --- Input Validations ---
    if (!currentUser?.uid) {
      setError("Authentication error. Please log in again.");
      return;
    }
    if (!selectedClassroomId) {
        setError("Please select a classroom to assign the test to.");
        return;
    }
    if (!title.trim()) {
        setError("Please enter a title for the test.");
        return;
    }
     if (!dueDate) {
        setError("Please select a due date and time for the test.");
        return;
    }
    // --- End Validations ---


    // --- Simplified Question Parsing Logic ---
    // This section parses the `questionsText` textarea.
    // Currently, it treats each non-empty line as a separate 'discursive' question.
    // This needs to be expanded significantly to handle different question types (MC, Associative)
    // and their specific data (options, columns, correct answers).
    const questionsData = questionsText
      .split('\n') // Split the text by new lines
      .map(line => line.trim()) // Remove leading/trailing whitespace from each line
      .filter(line => line.length > 0) // Keep only non-empty lines
      .map((text, index) => ({ // Map each line to a question object
        type: 'discursive', // *** HARDCODED as 'discursive' for now ***
        text: text,         // The question text is the line content
        order: index,       // Assign order based on line number
        points: 10          // *** Assign default points (e.g., 10) - adjust as needed ***
        // TODO: Implement parsing for other question types (MC, Associative)
        // This would involve defining a syntax in the textarea or, more likely,
        // building a dynamic form UI where teachers can add questions individually
        // and specify type, options, correct answers, etc.
      }));

    // Ensure at least one valid question was parsed
    if (questionsData.length === 0) {
        setError("Please add at least one question.");
        return;
    }
    // --- End Simplified Question Parsing ---

    setIsLoading(true); // Indicate submission is in progress

    // Prepare the main test data object for the service
    const testData = {
      title: title.trim(),
      // Convert the datetime-local string input value to a JavaScript Date object
      dueDate: new Date(dueDate),
      classroomId: selectedClassroomId
      // `questionOrder` will be generated by the service based on `questionsData` array order
    };

    try {
      // Call the service function to create the test and its questions
      const newTestId = await createTest(testData, currentUser.uid, questionsData);
      // Display success message including the new Test ID
      setSuccess(t('createTestPage.successMessage') + ` (ID: ${newTestId})`);
      // Optionally clear the form fields after successful creation
      setTitle('');
      setDueDate('');
      setSelectedClassroomId('');
      setQuestionsText('');
      // Optionally navigate to another page, e.g., a list of tests
      // navigate('/manage/tests');
    } catch (err) {
      // Display error message if test creation fails
      setError(t('createTestPage.errorCreateTest') + `: ${err.message}`);
      console.error("Error creating test:", err);
    } finally {
      setIsLoading(false); // Stop loading indicator
    }
  };

  // --- Render Component UI ---
  return (
    // Reusing container style from ManageClassroomsPage for layout consistency
    <div className="manage-classrooms-container">
      <h2>{t('createTestPage.title')}</h2> {/* Page Title */}

      {/* Display error messages */}
      {error && <p className="error-message">{error}</p>}
      {/* Display success messages */}
      {success && <p className="success-message">{success}</p>}

      {/* Test Creation Form - Reusing form style */}
      <form onSubmit={handleSubmit} className="classroom-form">

        {/* Test Title Input */}
        <div className="form-group">
          <label htmlFor="testTitle">{t('createTestPage.testTitleLabel')}</label>
          <input
            type="text"
            id="testTitle"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('createTestPage.testTitlePlaceholder')}
            required // HTML5 validation
            aria-required="true"
          />
        </div>

        {/* Due Date Input */}
        <div className="form-group">
          <label htmlFor="dueDate">{t('createTestPage.dueDateLabel')}</label>
          <input
            type="datetime-local" // Input type for selecting date and time
            id="dueDate"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            required
            aria-required="true"
          />
        </div>

        {/* Classroom Assignment Dropdown */}
        <div className="form-group">
          <label htmlFor="classroomSelect">{t('createTestPage.classroomLabel')}</label>
          <select
            id="classroomSelect"
            value={selectedClassroomId}
            onChange={(e) => setSelectedClassroomId(e.target.value)}
            required
            aria-required="true"
            // Disable dropdown if classrooms are loading or none are available
            disabled={managedClassrooms.length === 0 || isLoading}
          >
            {/* Default placeholder option */}
            <option value="" disabled>{t('createTestPage.selectClassroom')}</option>
            {/* Map through managed classrooms to create options */}
            {managedClassrooms.map(cls => (
              <option key={cls.id} value={cls.id}>{cls.name}</option>
            ))}
          </select>
        </div>

        {/* Questions Input (Simplified Textarea) */}
         <div className="form-group">
          <label htmlFor="questionsText">{t('createTestPage.questionsLabel')}</label>
          <textarea
            id="questionsText"
            rows="10" // Set initial height
            value={questionsText}
            onChange={(e) => setQuestionsText(e.target.value)}
            placeholder={t('createTestPage.questionsPlaceholder')}
            aria-label={t('createTestPage.questionsLabel')} // Accessibility label
          />
          {/* Future improvement: Add instructions for different question types or replace with dynamic form elements */}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          // Disable button while loading or if teacher manages no classrooms
          disabled={isLoading || managedClassrooms.length === 0}
        >
          {isLoading ? t('createTestPage.submitting') : t('createTestPage.submitButton')}
        </button>
      </form>
    </div>
  );
};

export default CreateTestPage;

